import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import type { AuditList, AuditVerification } from '@agentic-os/contracts';
import { Card, CardBody } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus, StatusBadge } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';

export function AuditPage() {
  const q = useInfiniteQuery({
    queryKey: ['audit'],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      api<AuditList>(`/api/v1/audit?limit=50${pageParam ? `&before=${pageParam}` : ''}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const verify = useMutation({ mutationFn: () => api<AuditVerification>('/api/v1/audit/verify') });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title="Audit"
        description="Tamper-evident log of security- and knowledge-relevant actions in your organization."
        actions={
          <Button onClick={() => verify.mutate()} disabled={verify.isPending}>
            <ShieldCheck className="size-3.5" aria-hidden />
            Verify chain
          </Button>
        }
      />
      {verify.data && (
        <p
          role="status"
          className="flex flex-wrap items-center gap-2 text-sm"
          data-testid="audit-verification"
        >
          <StatusBadge
            status={verify.data.valid ? 'ok' : 'fail'}
            label={verify.data.valid ? 'Chain intact' : 'Chain BROKEN'}
          />
          {verify.data.eventsChecked} events verified up to seq {verify.data.headSeq}
          {verify.data.reason && (
            <span className="text-fail">
              — {verify.data.reason} (seq {verify.data.firstInvalidSeq})
            </span>
          )}
        </p>
      )}
      {verify.isError && <ErrorNote error={verify.error} />}
      <Card>
        <CardBody className="p-0">
          {q.isError ? (
            <div className="p-4">
              <ErrorNote error={q.error} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Seq</Th>
                  <Th>Time</Th>
                  <Th>Action</Th>
                  <Th>Result</Th>
                  <Th>Actor</Th>
                  <Th>Resource</Th>
                  <Th>Correlation</Th>
                  <Th>Hash</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id}>
                    <Td className="text-muted tabular-nums">{e.seq}</Td>
                    <Td className="whitespace-nowrap text-muted">{formatTime(e.occurredAt)}</Td>
                    <Td>
                      <Mono>{e.action}</Mono>
                    </Td>
                    <Td>
                      <DomainStatus value={e.result} />
                    </Td>
                    <Td>
                      <Mono>
                        {e.actorType === 'user'
                          ? `${e.actorUserId?.slice(0, 8)}… via ${e.actorClientId}`
                          : e.actorService}
                      </Mono>
                    </Td>
                    <Td>
                      <Mono>{`${e.resourceType}${e.resourceId ? `:${e.resourceId.slice(0, 12)}` : ''}`}</Mono>
                    </Td>
                    <Td>
                      <Mono>{e.correlationId?.slice(0, 8) ?? '—'}</Mono>
                    </Td>
                    <Td>
                      <Mono title={e.hash}>{e.hash.slice(0, 10)}…</Mono>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
      {q.hasNextPage && (
        <Button onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          Load more
        </Button>
      )}
    </div>
  );
}
